import { useCallback, useContext, useMemo, useRef } from 'react'

import {
  Box,
  Chip,
  darken,
  IconButton,
  lighten,
  Stack,
  Typography,
} from '@mui/material'
import {
  useGridApiContext,
  type GridPinnedColumnPosition,
} from '@mui/x-data-grid-premium'
import {
  IconArrowBigDownFilled,
  IconArrowBigUpFilled,
  IconChartFunnel,
  IconEyeOff,
  IconPinFilled,
  IconSettings,
  IconSparkles,
  IconTriangleSquareCircleFilled,
  IconX,
} from '@tabler/icons-react'
import { useConfirm } from 'material-ui-confirm'
import toast from 'react-hot-toast'
import { isUUID } from 'validator'

import { useMutation } from '@redwoodjs/web'

import { canManageObjectType } from 'src/components/Properties/access'
import { BACKFILL_PROPERTY_DEFINITION } from 'src/components/Properties/mutations'
import Row from 'src/components/Row/Row'
import { objectPropertyDefinitionMetadata } from 'src/components/Sidebar/SidebarObjectCreate/objectPropertyDefinitions'
import PropertyTypeIcon from 'src/components/Views/PropertyTypeIcon'
import { colorStyles } from 'src/components/Views/styles'
import { DayContext } from 'src/lib/dayContext'
import { logger } from 'src/lib/logger'
import { NativeObjectTypes } from 'src/lib/objects'
import { shadows } from 'src/lib/style'

import useObjects from '../useObjects'

const containerSx = {
  display: 'flex',
  flexDirection: 'column',
  height: 'auto',
  maxHeight: '600px',
  width: '280px',
  border: (theme) => `1px solid ${theme.palette.divider}`,
  py: '8px',
  px: '12px',

  '& .column-menu-header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
    pb: '4px',
    height: '40px',
    '& .MuiTypography-root': {
      fontSize: '13px',
      fontWeight: 600,
      letterSpacing: '-0.3px',
    },
  },

  '& .column-type-chip': {
    width: '100%',
    borderRadius: '4px',
    flexShrink: 0,
    borderColor: (theme) => theme.palette.columns.light,
    backgroundColor: (theme) => lighten(theme.palette.columns.light, 0.8),
    color: (theme) => darken(theme.palette.columns.light, 0.4),
    justifyContent: 'flex-start',
    pl: '8px',
    '& .MuiChip-icon': {
      ml: '0px',
      mr: '4px',
    },
    '& .MuiChip-label': {
      fontSize: '11px',
      fontWeight: 500,
    },
  },

  '& .column-type-container': {
    border: (theme) => `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    boxShadow: (theme) => shadows[theme.palette?.mode]?.lg,
    '& .column-type-header': {
      borderBottom: 'none',
      borderBottomLeftRadius: '0px',
      borderBottomRightRadius: '0px',
      backgroundColor: (theme) => theme.palette.columns.light,
      color: (theme) => theme.palette.columns.contrastText,
      px: '8px',
      py: '2px',
      gap: '4px',
      '& .MuiTypography-root': {
        fontSize: '10px',
        fontWeight: 600,
        letterSpacing: '-0.22px',
        color: (theme) => theme.palette.columns.contrastText,
      },
    },
    '& .column-description-text': {
      fontSize: '11px',
      fontStyle: 'italic',
      fontWeight: 400,
      letterSpacing: '-0.22px',
      p: '8px',
      borderRadius: '4px',
      borderTopLeftRadius: '0px',
      borderTopRightRadius: '0px',
    },
  },

  '& .action-section-title': {
    fontSize: '11px',
    fontWeight: 600,
    color: 'text.secondary',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    mb: 0.5,
    display: 'none',
  },

  '& .action-chip-stack': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2px',

    '& .pinned': {
      width: '48px',
      flexShrink: 1,
    },
    '& .hide': {
      justifyContent: 'flex-start',
      width: '60px',
      minWidth: '60px',
      flex: 0,
      flexShrink: 0,
      pr: 0,
      '& .MuiChip-label': {
        pl: '8px',
        pr: '4px',
      },
    },
  },

  '& .MuiChip-root': {
    height: '26px',
    minHeight: '26px',
    flex: 1,
    justifyContent: 'space-between',
    '&.currently-active': colorStyles,
    '&.not-active': {
      borderColor: (theme) => theme.palette.action.disabledBackground,
      color: (theme) => theme.palette.text.secondary,
      '& .MuiChip-icon': {
        color: (theme) => theme.palette.text.secondary,
      },
    },
    '& .MuiChip-label': {
      fontSize: '11px',
      fontWeight: 500,
      px: '8px',
    },
    '& .MuiChip-icon': {
      marginLeft: '6px',
      marginRight: '-4px',
      height: '12px',
      width: '12px',
      flexShrink: 0,
      color: 'inherit',
    },
    '& .MuiChip-deleteIcon': {
      marginRight: '4px',
      marginLeft: '-4px',
      height: '14px',
      width: '14px',
      opacity: 0,
    },

    '&.backfill-property': {
      justifyContent: 'flex-start',
    },

    '&.edit-property': {
      width: '64px',
      flex: 0,
    },
  },

  '& .pinned-left': {
    transform: 'rotateY(180deg)',
  },

  '& .column-actions': {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    mt: 3,
    mb: 2,
  },
}

const ObjectsColumnMenu = (params) => {
  const confirm = useConfirm()
  const { propertyDefinitions } = useObjects()
  const { setSidebarObject, workspaces } = useContext(DayContext)
  const apiRef = useGridApiContext()
  const anchorEl = useRef<HTMLElement | null>(null)

  const [runTest] = useMutation(BACKFILL_PROPERTY_DEFINITION)

  const allColumns = apiRef.current.getAllColumns()

  const objectType = params?.objectType
  const columnHeaderName = params?.colDef?.headerName
  const columnField = allColumns.find(
    (c) => c.headerName === columnHeaderName && !c.field.startsWith('__')
  )?.field

  const propertyDefinitionId = columnField
  const propertyDefinition =
    propertyDefinitions[objectType]?.[propertyDefinitionId]

  const propertyTypeId = propertyDefinition.propertyTypeId

  const gridState = apiRef.current.exportState()
  const sortModel = gridState.sorting.sortModel
  const filterModel = gridState.filter.filterModel.items
  const groupModel = gridState.rowGrouping.model
  const pinnedColumns = gridState.pinnedColumns

  const columnSort = sortModel.find((s) => s.field === propertyDefinitionId)
  const isSortedAsc = columnSort?.sort === 'asc'
  const isSortedDesc = columnSort?.sort === 'desc'

  const columnFilters = filterModel.filter(
    (f) => f.field === propertyDefinitionId
  )

  const isGrouped = groupModel.includes(propertyDefinitionId)

  const isPinnedLeft = pinnedColumns?.left?.includes(propertyDefinitionId)
  const isPinnedRight = pinnedColumns?.right?.includes(propertyDefinitionId)

  logger.dev('ObjectsColumnMenu', {
    propertyDefinitionId,
    pinnedColumns,
    isPinnedLeft,
    isPinnedRight,
  })

  const handleSort = useCallback(
    (direction: 'asc' | 'desc') => {
      const currentSort = sortModel.find(
        (s) => s.field === propertyDefinitionId
      )
      let newSortModel = sortModel.filter(
        (s) => s.field !== propertyDefinitionId
      )

      if (!(currentSort?.sort === direction)) {
        newSortModel = [
          //...newSortModel,
          { field: propertyDefinitionId, sort: direction },
        ]
      }

      apiRef.current.setSortModel(newSortModel)
    },
    [sortModel, propertyDefinitionId, apiRef]
  )

  const handleFilterAdd = useCallback(() => {
    logger.dev('handleFilterAdd', propertyDefinitionId)
    apiRef.current.showFilterPanel(propertyDefinitionId)
    params.hideMenu()
  }, [propertyDefinitionId, params, apiRef])

  const handleFilterRemove = useCallback(
    (filterToRemove) => {
      const newFilters = filterModel.filter(
        (f) =>
          !(
            f.field === filterToRemove.field &&
            f.operator === filterToRemove.operator &&
            String(f.value) === String(filterToRemove.value)
          )
      )
      apiRef.current.setFilterModel({ items: newFilters })
    },
    [filterModel, apiRef]
  )

  const handleGroupingToggle = useCallback(() => {
    const newGroupingModel = isGrouped ? [] : [propertyDefinitionId]
    apiRef.current.setRowGroupingModel(newGroupingModel)

    apiRef.current.sortColumn('__row_group_by_columns_group__', 'desc')
  }, [isGrouped, propertyDefinitionId, apiRef])

  const handlePin = (direction: 'left' | 'right' | null) => {
    const currentlyPinned = isPinnedLeft || isPinnedRight
    const pinningToSameDirection =
      (direction === 'left' && isPinnedLeft) ||
      (direction === 'right' && isPinnedRight)

    if (currentlyPinned) {
      apiRef.current.unpinColumn(propertyDefinitionId)
    }

    if (direction && !pinningToSameDirection) {
      apiRef.current.pinColumn(
        propertyDefinitionId,
        direction as GridPinnedColumnPosition
      )
    }
    params.hideMenu()
  }

  const handleOpenInSidebar = useCallback(() => {
    setSidebarObject({
      objectType: NativeObjectTypes.PropertyDefinition,
      mode: 'edit',
      objectId: propertyDefinition.id,
      workspaceId: propertyDefinition.workspaceId,
      properties: {
        id: propertyDefinition.id,
        name: propertyDefinition.name,
        description: propertyDefinition.description,
        objectTypeId: propertyDefinition.objectTypeId,
        propertyTypeId: propertyDefinition.propertyTypeId,
        propertyType: propertyDefinition.propertyType || {
          id: propertyDefinition.propertyTypeId,
        },
        aiManaged: propertyDefinition.aiManaged,
        useWeb: propertyDefinition.useWeb,
        options: propertyDefinition.options,
        createdAt: propertyDefinition.createdAt,
        updatedAt: propertyDefinition.updatedAt,
      },
    })
    apiRef.current.hideColumnMenu()
  }, [setSidebarObject, propertyDefinition, apiRef])

  const propertyWorkspaceForUser = useMemo(() => {
    return (Array.isArray(workspaces) ? workspaces : []).find(
      (workspace) => workspace.id === propertyDefinition.workspaceId
    )
  }, [workspaces, propertyDefinition])

  const userCanManageObjectType = useMemo(() => {
    return canManageObjectType(propertyDefinition, propertyWorkspaceForUser)
  }, [propertyDefinition, propertyWorkspaceForUser])

  const handleRunBackfill = async () => {
    if (!userCanManageObjectType) return

    try {
      await confirm({
        title: 'Run Property Backfill',
        description: `Would you like to run a backfill to populate existing records with ${
          propertyDefinition.aiManaged && propertyDefinition.useWeb
            ? 'AI and web research'
            : propertyDefinition.aiManaged
              ? 'AI analysis'
              : 'web research'
        }?`,
        confirmationText: 'Run Backfill',
        cancellationText: 'Skip',
      })
      toast.promise(
        runTest({
          variables: {
            workspaceId: propertyDefinition.workspaceId,
            objectType: propertyDefinition.objectTypeId,
            propertyDefinitionId: propertyDefinition.id,
          },
        }),
        {
          loading: 'Setting up backfill...',
          success: () => {
            return 'Backfill is running'
          },
          error: 'Failed to run backfill',
        }
      )
    } catch (_) {
      // User did not confirm to run backfill
    }
  }

  const isCustomProperty = useMemo(() => {
    return isUUID(propertyDefinition.id)
  }, [propertyDefinition])

  return (
    propertyDefinition && (
      <Box
        sx={containerSx}
        ref={anchorEl}
      >
        <Row className="column-menu-header">
          <Typography>{propertyDefinition.name}</Typography>
          <IconButton
            onClick={params.hideMenu}
            size="small"
          >
            <IconX size={16} />
          </IconButton>
        </Row>

        <Box className="column-type-container">
          <Row className="column-type-header">
            <PropertyTypeIcon
              propertyTypeId={propertyTypeId}
              objectType={objectType}
              size={12}
            />
            <Typography className="column-type-label">
              {isCustomProperty ? 'Custom ' : 'Standard '}
              {objectPropertyDefinitionMetadata[propertyTypeId]?.name ??
                'Unknown Type'}
              {' Property'}
            </Typography>
          </Row>

          <Typography className="column-description-text">
            {propertyDefinition.description}

            {params?.colDef?.editable ? ' (editable)' : ' (non-editable)'}
          </Typography>
        </Box>

        <Box className="column-actions">
          <Box>
            <Typography className="action-section-title">Sort</Typography>
            <Stack
              direction="row"
              spacing={0.5}
              className="action-chip-stack"
            >
              <Chip
                icon={<IconArrowBigUpFilled size={12} />}
                label="Ascending"
                size="small"
                variant="outlined"
                onClick={() => handleSort('asc')}
                className={`sorts save-on-click ${isSortedAsc ? 'currently-active' : 'not-active'}`}
                deleteIcon={<IconX size={12} />}
                onDelete={() => {}}
                disabled={!params?.colDef?.sortable}
              />
              <Chip
                icon={<IconArrowBigDownFilled size={12} />}
                label="Descending"
                size="small"
                variant="outlined"
                onClick={() => handleSort('desc')}
                className={`sorts save-on-click ${isSortedDesc ? 'currently-active' : 'not-active'}`}
                deleteIcon={<IconX size={12} />}
                onDelete={() => {}}
                disabled={!params?.colDef?.sortable}
              />
            </Stack>
          </Box>

          <Box>
            <Typography className="action-section-title">Filter</Typography>
            <Stack
              direction="column"
              spacing={0.5}
              className="action-chip-stack"
            >
              {columnFilters.map((filter) => (
                <Chip
                  key={`filter-${filter.field}-${filter.operator}-${filter.value}`}
                  label={`Filtered for: ${filter.operator} "${filter.value}"`}
                  size="small"
                  icon={<IconChartFunnel size={12} />}
                  variant="outlined"
                  className="filters save-on-click currently-active"
                  onDelete={() => handleFilterRemove(filter)}
                  deleteIcon={<IconX size={12} />}
                  onClick={() => {}}
                  disabled={!params?.colDef?.filterable}
                />
              ))}
              <Chip
                icon={<IconChartFunnel size={12} />}
                label="Use to filter data"
                size="small"
                variant="outlined"
                onClick={handleFilterAdd}
                className="filters not-active"
                deleteIcon={<IconX size={12} />}
                onDelete={() => {}}
                disabled={!params?.colDef?.filterable}
              />
            </Stack>
          </Box>

          <Box>
            <Typography className="action-section-title">Group</Typography>
            <Stack
              direction="row"
              spacing={0.5}
              className="action-chip-stack"
            >
              <Chip
                icon={<IconTriangleSquareCircleFilled size={12} />}
                label={isGrouped ? 'Grouped' : 'Group by property'}
                size="small"
                variant="outlined"
                onClick={handleGroupingToggle}
                className={`groupings save-on-click ${isGrouped ? 'currently-active' : 'not-active'}`}
                onDelete={() => {}}
                deleteIcon={<IconX size={12} />}
                disabled={!params?.colDef?.groupable}
              />
            </Stack>
          </Box>

          <Box>
            <Typography className="action-section-title">Pin</Typography>
            <Stack
              direction="row"
              spacing={0.5}
              className="action-chip-stack"
            >
              <Chip
                icon={
                  <IconPinFilled
                    size={12}
                    className="pinned-left"
                  />
                }
                label="Pin left"
                size="small"
                variant="outlined"
                onClick={() => handlePin('left')}
                className={`pinned save-on-click ${isPinnedLeft ? 'currently-active' : 'not-active'}`}
                deleteIcon={<IconX size={12} />}
                onDelete={() => {}}
                disabled={!params?.colDef?.pinnable}
              />
              <Chip
                label="Hide"
                size="small"
                icon={<IconEyeOff size={12} />}
                variant="outlined"
                className="hide not-active"
                onClick={() => {
                  apiRef.current.setColumnVisibility(
                    propertyDefinitionId,
                    false
                  )
                  params.hideMenu()
                }}
              />
              <Chip
                icon={<IconPinFilled size={12} />}
                label="Pin right"
                size="small"
                variant="outlined"
                onClick={() => handlePin('right')}
                className={`pinned save-on-click ${isPinnedRight ? 'currently-active' : 'not-active'}`}
                deleteIcon={<IconX size={12} />}
                onDelete={() => {}}
                disabled={!params?.colDef?.pinnable}
              />
            </Stack>
          </Box>
          {userCanManageObjectType && (
            <Box>
              <Stack
                direction="row"
                spacing={0.5}
                className="action-chip-stack"
              >
                <Chip
                  label="Populate with AI"
                  icon={<IconSparkles size={12} />}
                  className={`backfill-property not-active`}
                  variant="outlined"
                  onClick={handleRunBackfill}
                />
                <Chip
                  icon={<IconSettings size={12} />}
                  label="Edit"
                  onClick={handleOpenInSidebar}
                  className={`edit-property not-active`}
                  variant="outlined"
                />
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    )
  )
}

export default ObjectsColumnMenu
